import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Plus, Pencil, Power, Trash2, MapPin, DollarSign, Layers, Loader2, ExternalLink } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
};
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");

const asList = (v) => (Array.isArray(v) ? v : v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : []);
const markets = (b) => {
  const parts = [...asList(b.cities_wanted), ...asList(b.counties_wanted)];
  return parts.length ? parts.slice(0, 3).join(" · ") + (parts.length > 3 ? ` +${parts.length - 3}` : "") : "—";
};
const acceptedTypes = (b) => {
  const out = [];
  if (b.raw_land_accepted === true || b.raw_land_accepted === "Yes") out.push("Raw Land");
  if (b.entitled_lots_accepted === true || b.entitled_lots_accepted === "Yes") out.push("Entitled Lots");
  if (b.finished_lots_accepted === true || b.finished_lots_accepted === "Yes") out.push("Finished Lots");
  return out;
};
const isActive = (b) => !(b.active === false || b.active === "No");

export default function BuilderBuyBox() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/builder_buy_boxes?limit=500&sort_by=builder_name&sort_dir=asc");
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (b) => {
    try {
      await api.put(`/builder_buy_boxes/${b.id}`, { active: !isActive(b) });
      toast.success(isActive(b) ? "Builder deactivated" : "Builder activated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/builder_buy_boxes/${toDelete.id}?hard=true`);
      toast.success("Builder deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="builder-buy-box-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Landmark className="h-4 w-4" /> Operations
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Builder Buy Boxes</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Home builders and the land they want to buy — capture their criteria by market.
          </p>
        </div>
        <Button data-testid="bbb-new-btn" onClick={() => nav("/builder-buy-box/new")} className="shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> New Builder
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="bbb-empty">
          <Landmark className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="text-lg font-medium">No builders yet</h3>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            Add your first builder to capture what land they want and where.
          </p>
          <Button data-testid="bbb-new-empty-btn" onClick={() => nav("/builder-buy-box/new")}>
            <Plus className="h-4 w-4 mr-1.5" /> New Builder
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((b) => (
            <Card key={b.id} onClick={() => nav(`/builder-buy-box/${b.id}/edit`)} className="p-5 hover:shadow-md transition-shadow cursor-pointer" data-testid={`bbb-card-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-lg truncate">{b.builder_name || "Untitled Builder"}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5" /> {markets(b)}
                  </div>
                  {(b.contact_name || b.title) && (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{[b.contact_name, b.title].filter(Boolean).join(" · ")}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Badge variant="outline" className={isActive(b) ? STATUS_STYLE.active : STATUS_STYLE.inactive}>
                    {isActive(b) ? "active" : "inactive"}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 px-2" data-testid={`bbb-menu-${b.id}`}>•••</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => toggleStatus(b)}>
                        <Power className="h-4 w-4 mr-2" /> {isActive(b) ? "Deactivate" : "Activate"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" data-testid={`bbb-delete-${b.id}`} onClick={() => setToDelete(b)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> Target Lot Price</div>
                  <div className="font-medium">{money(b.target_finished_lot_price)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><Layers className="h-3 w-3" /> Product</div>
                  <div className="font-medium">{b.target_product_type || "—"}</div>
                </div>
              </div>

              {acceptedTypes(b).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {acceptedTypes(b).map((t) => (
                    <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t}</span>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this builder?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.builder_name}" will be permanently removed from your Builder Buy Box.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="bbb-delete-confirm" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
